import React, { useState } from 'react';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  TextField,
  Alert,
  Paper,
  Stack,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';

export default function SplitPdfPage() {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [pagesPerPart, setPagesPerPart] = useState('120');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!file) {
      setError('Choose a PDF file first.');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('password', password);
      formData.append('pages_per_part', pagesPerPart || '120');

      const res = await axios.post(`${API_URL}/split-pdf/`, formData, {
        responseType: 'blob',
        validateStatus: () => true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.status >= 400) {
        let msg = 'Could not split PDF.';
        try {
          const text = await res.data.text();
          const j = JSON.parse(text);
          if (typeof j.detail === 'string') msg = j.detail;
          else if (j.detail && typeof j.detail === 'object') msg = j.detail.message || JSON.stringify(j.detail);
        } catch {
          /* keep default */
        }
        throw new Error(msg);
      }

      const cd = res.headers['content-disposition'];
      let downloadName = 'statement_split.zip';
      if (cd && cd.includes('filename=')) {
        const m = cd.match(/filename="?([^";]+)"?/);
        if (m) downloadName = m[1];
      }

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess('Download started. Unzip the files, then upload each PDF on the Analyzer tab (one part at a time or combine as you prefer).');
    } catch (err) {
      setError(err.message || 'Split failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Button component="a" href="/" startIcon={<ArrowBackIcon />} color="inherit" sx={{ mr: 2 }}>
            Home
          </Button>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Split large PDF
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
          Split a statement PDF into smaller files
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload one PDF. We create a ZIP of smaller PDFs (by default <strong>120 pages</strong> each, longest part last).
          Parts after the first include an extra copy of the document’s <strong>first page</strong> at the start so column
          headers are present for analysis (when you merge results, duplicates from that page are dropped by normal deduplication).
          This step does <strong>not</strong> use your analysis page quota. Download the ZIP, extract the files, then upload
          each part on the <strong>Analyzer</strong> (or Dashboard) for analysis. Password-protected PDFs are supported if you
          enter the same password.
        </Typography>

        <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Stack spacing={2}>
            <Button variant="outlined" component="label">
              Choose PDF (max 100MB)
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            {file && (
              <Typography variant="body2" color="text.secondary">
                Selected: <strong>{file.name}</strong>
              </Typography>
            )}
            <TextField
              label="PDF password (if protected)"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              autoComplete="off"
            />
            <TextField
              label="Pages per file"
              type="number"
              value={pagesPerPart}
              onChange={(e) => setPagesPerPart(e.target.value)}
              helperText="Between 10 and 500 (default 120)."
              inputProps={{ min: 10, max: 500 }}
            />
            <Button type="submit" variant="contained" color="primary" disabled={loading || !file}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Download ZIP of split PDFs'}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
